import React, { useState } from 'react';
import { Card, Form, Button, Spinner } from 'react-bootstrap';
import { Play, Database } from 'lucide-react';

import SiteIdInput from './SiteIdInput';
import StatusNotification from './StatusNotification';
import { validateSiteIds } from '../utility/validators';
import { scheduleIndexing, getIndexStatus } from '../services/api';

const ManualIndexing = () => {
    // State for managing site IDs input
    const [siteIdInput, setSiteIdInput] = useState('');
    // State for tracking if indexing is in progress
    const [isIndexing, setIsIndexing] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [notification, setNotification] = useState(null);
    const [indexStatus, setIndexStatus] = useState({});
    const [statusCheckSuccess, setStatusCheckSuccess] = useState(false);

    /**
     * Formats API response into user-friendly notification message
     * @param {Object} data - API response with success and failed arrays
     * @returns {Object} - Formatted notification object
     */
    const formatResponseMessage = (data) => {
        if (!data || !data.success || !data.failed) {
            return {
                type: 'success',
                message: 'Indexing scheduled for the requested sites.',
            };
        }

        const successSites = data.success.join(', ');
        const failedSites = data.failed.join(', ');

        let message = '';

        if (data.success.length > 0) {
            message = `Indexing scheduled successfully for sites: ${successSites}.`;
        }

        if (data.failed.length > 0) {
            message += `${data.success.length > 0 ? '\n\n' : ''}Indexing not scheduled for invalid site IDs: ${failedSites}.`;
        }

        return {
            type: data.success.length > 0 ? 'success' : 'danger',
            message: message,
            useHtml: true,
        };
    };

    /**
     * Handles errors during indexing operation
     * @param {Object} error - Error object from API call
     * @returns {Object} - Formatted error notification
     */
    const handleIndexingError = (error) => {
        console.error('Indexing API Error:', error);
        return {
            type: 'danger',
            message: 'Could not schedule indexing for sites.',
        };
    };

    /**
     * Handles errors during status check operation
     * @param {Object} error - Error object from API call
     * @returns {Object} - Formatted error notification
     */
    const handleStatusCheckError = (error) => {
        console.error('Status Check API Error:', error);
        return {
            type: 'danger',
            message: 'Could not retrieve Indexing Status',
        };
    };

    // Function to handle the Index Now button click - schedules indexing
    const handleIndexNow = async () => {
        // Validate input using our utility function
        const validation = validateSiteIds(siteIdInput);

        if (!validation.isValid) {
            setNotification({ type: 'danger', message: validation.error });
            return;
        }

        // Set loading state
        setIsIndexing(true);
        setNotification(null);
        setStatusCheckSuccess(false); // Hide the table

        try {
            console.log('Scheduling indexing for sites:', validation.siteIds);

            // Call our API service
            const data = await scheduleIndexing(validation.siteIds);

            // Format response message
            const formattedResponse = formatResponseMessage(data);
            setNotification(formattedResponse);

            // If we reached this point, it means we got a successful HTTP 200 response
            // (since errors in the API call would trigger the catch block)
            // We'll clear the input only when the operation was successful
            setSiteIdInput('');
        } catch (error) {
            setNotification(handleIndexingError(error));
        } finally {
            // Reset loading state
            setIsIndexing(false);
        }
    };

    const handleCheckStatus = async () => {
        setIsCheckingStatus(true);
        setNotification(null);
        setStatusCheckSuccess(false);

        try {
            let siteIds = [];
            if (siteIdInput.trim()) {
                const validation = validateSiteIds(siteIdInput);
                if (!validation.isValid) {
                    setNotification({
                        type: 'danger',
                        message: validation.error,
                    });
                    return;
                }
                siteIds = validation.siteIds;
            }

            const statusData = await getIndexStatus(
                siteIds.length > 0 ? siteIds : undefined
            );
            setIndexStatus(statusData);
            setStatusCheckSuccess(true);
            setNotification({
                type: 'success',
                message: 'Indexing Status retrieved successfully',
            });
        } catch (error) {
            setNotification(handleStatusCheckError(error));
            setIndexStatus({});
        } finally {
            setIsCheckingStatus(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === '0' || dateString === 0)
            return 'Not Indexed';
        try {
            // Remove any milliseconds and timezone information
            const cleanDate = dateString.split('.')[0].replace('Z', '');
            return cleanDate.replace('T', ' ');
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    return (
        <div className="space-y-6">
            {/* Input Section */}
            <Card className="bg-card shadow-sm">
                <Card.Header className="text-left">
                    <Card.Title className="font-medium text-foreground text-left">
                        Enter Site ID(s)
                    </Card.Title>
                </Card.Header>
                <Card.Body className="space-y-4">
                    <Form>
                        {/* Use our reusable SiteIdInput component */}
                        <SiteIdInput
                            value={siteIdInput}
                            onChange={setSiteIdInput}
                        />

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <Button
                                variant="primary"
                                onClick={handleIndexNow}
                                disabled={
                                    isIndexing ||
                                    !siteIdInput.trim() ||
                                    isCheckingStatus
                                }
                                className="flex items-center gap-2"
                            >
                                {isIndexing ? (
                                    <>
                                        <Spinner
                                            className="me-2"
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <Play size={16} className="me-2" />
                                        <b>Index Now</b>
                                    </>
                                )}
                            </Button>

                            <Button
                                variant="outline-secondary"
                                onClick={handleCheckStatus}
                                disabled={
                                    isCheckingStatus ||
                                    isIndexing ||
                                    !siteIdInput.trim()
                                }
                                className="flex items-center gap-2"
                            >
                                {isCheckingStatus ? (
                                    <>
                                        <Spinner
                                            className="me-2"
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <Database size={16} className="me-2" />
                                        <b>Index Status</b>
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Use our StatusNotification component */}
                        <StatusNotification notification={notification} />
                    </Form>
                </Card.Body>
            </Card>

            {/* Status Table - Only show if status check was successful */}
            {statusCheckSuccess && Object.keys(indexStatus).length > 0 && (
                <Card className="bg-card shadow-sm">
                    <Card.Header className="border-b border-border">
                        <Card.Title className="font-medium text-foreground text-left">
                            Account Indexing Status
                        </Card.Title>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-left">
                                <thead>
                                    <tr className="border-b border-border">
                                        <th className="p-3 font-medium text-foreground text-left whitespace-nowrap">
                                            Site ID
                                        </th>
                                        <th className="p-3 font-medium text-foreground text-left whitespace-nowrap">
                                            Last Indexed (in UTC)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(indexStatus).map(
                                        ([siteId, data]) => (
                                            <tr
                                                key={siteId}
                                                className="border-b border-border hover:bg-muted/30"
                                            >
                                                <td className="p-3 font-mono text-sm text-left">
                                                    {siteId}
                                                </td>
                                                <td
                                                    className={`p-3 text-sm text-left ${
                                                        data.errorMessage
                                                            ? 'text-destructive'
                                                            : formatDate(
                                                                    data.lastIndexedAt
                                                                ) ===
                                                                'Not Indexed'
                                                              ? 'text-warning'
                                                              : ''
                                                    }`}
                                                >
                                                    {data.errorMessage ||
                                                        formatDate(
                                                            data.lastIndexedAt
                                                        )}
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* No Data State - Only show if status check was successful but no data */}
            {statusCheckSuccess && Object.keys(indexStatus).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                    <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No indexing data available.</p>
                </div>
            )}

            {/* Footer */}
            <div className="text-center py-4">
                <p className="text-sm text-muted-foreground italic">
                    Note: Data is based on the last successful indexing run
                    recorded in DynamoDB.
                </p>
            </div>
        </div>
    );
};

export default ManualIndexing;
